import * as React from "react";
import {
  BasePostRequest,
  Media,
  MediaType,
  MediaGroup,
  PlayerWithBranch,
  SendModeType,
  SocialNetworksId,
} from "@/interfaces";
import { PlayersServices, SocialMentionData } from "@/services/bedrock";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { SharedPostFlowDefaults } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { promptSpecificDate, useSocialCoachSession } from "@/utils";
import { useBuiltCaption } from "./useBuiltCaption";

export interface EffectivePlayer {
  fullName: string | null;
  photoUrl: string | null;
  complianceDisclaimer: string | null;
}

/** The player a post is made for on their behalf; shared with screens that title the flow by their name. */
export const postFlowPlayerQuery = (playerId: number | undefined) => ({
  queryKey: ["player", playerId, "postFlow"] as const,
  queryFn: () => PlayersServices.getPlayer(playerId as number, ["shortLink"]),
});

export interface DeliveryOptions {
  isAutoPost: boolean;
  sendMode: SendModeType;
  socialNetworks: SocialNetworksId[];
  date?: Date;
  time?: Date;
}

export interface SharedPostFlowProps {
  defaults: SharedPostFlowDefaults;
}

export function useSharedPostFlow({ defaults }: SharedPostFlowProps) {
  const {
    isPlayer,
    playerId: sessionPlayerId,
    coachId: sessionCoachId,
    userId: sessionUserId,
  } = useSocialCoachSession();

  const onBehalfOf = defaults?.onBehalfOf || false;
  const effectivePlayerId = defaults?.playerId ?? (sessionPlayerId != null ? Number(sessionPlayerId) : undefined);

  // On behalf of a player, the name, photo, disclaimer, and user id are the player's, not the session's.
  const playerQuery = useQuery({
    ...postFlowPlayerQuery(effectivePlayerId),
    enabled: onBehalfOf && effectivePlayerId != null,
  });
  const effectivePlayer: EffectivePlayer | undefined = playerQuery.data
    ? {
        fullName: playerQuery.data.fullName,
        photoUrl: playerQuery.data.photoUrl,
        complianceDisclaimer: playerQuery.data.complianceDisclaimer ?? null,
      }
    : undefined;

  // The player's user id is only known for the session player until the on-behalf flow fetches it.
  const effectivePlayerUserId =
    defaults?.playerUserId ??
    (isPlayer && sessionUserId != null ? Number(sessionUserId) : undefined) ??
    playerQuery.data?.userId;

  const [defaultMessage, setDefaultMessage] = React.useState(defaults?.message);
  const [message, setMessage] = React.useState(defaults?.message);
  const [mediaGroup, setMediaGroup] = React.useState<MediaGroup | undefined>(defaults?.mediaGroup);
  const [hashtags, setHashtags] = React.useState(defaults?.hashtags || "");
  const [mentions, setMentions] = React.useState<SocialMentionData[]>([]);
  const [isPosting, setIsPosting] = React.useState(false);
  const [postError, setPostError] = React.useState<string | undefined>(undefined);
  const [pendingCaption, setPendingCaption] = React.useState<string | null>(null);
  const [blockedWords, setBlockedWords] = React.useState<string[]>([]);
  const [flaggedWords, setFlaggedWords] = React.useState<string[]>([]);
  // Set once the player edits the caption, so a caption that arrives later never overwrites their text.
  const [messageTouched, setMessageTouched] = React.useState(false);
  const messageTouchedRef = React.useRef(false);
  messageTouchedRef.current = messageTouched;

  const resetMessageTouched = React.useCallback(() => setMessageTouched(false), []);
  const applyGeneratedCaption = React.useCallback((caption: string | null) => {
    if (caption == null || messageTouchedRef.current) return;
    setDefaultMessage(caption);
  }, []);

  const addDisclaimer = defaults?.addDisclaimer !== false;
  const addDefaultHashtags = defaults?.addDefaultHashtags !== false;
  const addShortLink = defaults?.addShortLink !== false;

  const { builderLoading } = useBuiltCaption({
    defaultMessage,
    setMessage,
    hashtags,
    addShortLink,
    addDisclaimer,
    addDefaultHashtags,
    playerId: effectivePlayerId,
    coachId: sessionCoachId,
    keepEdits: messageTouched,
    onBehalfOf,
    playerDisclaimer: effectivePlayer?.complianceDisclaimer,
    fromPrompt: defaults?.promptId != null,
  });

  // Player search (only used by coach flow)
  const [selectedPlayerIds, setSelectedPlayerIds] = React.useState<number[]>([]);
  const [players, setPlayers] = React.useState<PlayerWithBranch[]>([]);
  const selectedPlayers = React.useMemo(() => {
    return players.filter(p => selectedPlayerIds.includes(p.info.playerId!));
  }, [players, selectedPlayerIds]);

  const media = React.useMemo(() => {
    return (mediaGroup?.media || []).sort((m1, m2) => m1.sortOrder - m2.sortOrder);
  }, [mediaGroup]);

  const getDefaultScheduleTime = () => {
    const date = new Date();

    // 1. Add 5 minutes to satisfy the "at least 5 minutes in the future" rule
    date.setMinutes(date.getMinutes() + 5);

    // 2. Calculate the next 30-minute interval
    const minutes = date.getMinutes();
    const next30MinInterval = Math.ceil(minutes / 30) * 30;

    // 3. Apply the rounded minutes and zero out seconds/milliseconds for a clean timestamp
    date.setMinutes(next30MinInterval, 0, 0);

    return date;
  };

  const [deliveryOptions, setDeliveryOptions] = React.useState<DeliveryOptions>(() => {
    const defaultTime = getDefaultScheduleTime();

    return {
      isAutoPost: true,
      sendMode: "IMMEDIATE",
      socialNetworks: [],
      date: defaultTime,
      time: defaultTime,
    };
  });
  const effectiveSocialNetworks: SocialNetworksId[] = deliveryOptions.isAutoPost ? deliveryOptions.socialNetworks : [];
  const effectiveTotalPostCount = React.useMemo(() => {
    const playerCount = isPlayer || onBehalfOf ? 1 : selectedPlayerIds.length;
    const channelCount = deliveryOptions.isAutoPost ? deliveryOptions.socialNetworks.length : 1;
    return channelCount * playerCount;
  }, [selectedPlayerIds, deliveryOptions?.isAutoPost, deliveryOptions?.socialNetworks?.length, onBehalfOf]);

  const isScheduledIncomplete =
    deliveryOptions.sendMode === "SCHEDULED" && !(deliveryOptions.date && deliveryOptions.time);
  const isDeliveryStepIncomplete =
    (deliveryOptions.isAutoPost && effectiveSocialNetworks.length < 1) || isScheduledIncomplete;
  const postButtonLabel =
    deliveryOptions.sendMode == "SCHEDULED"
      ? "Confirm & Schedule"
      : deliveryOptions.isAutoPost
      ? "Confirm & Post"
      : "Confirm & Send";

  const addMention = (result: SocialMentionData) => {
    const existingIndex = mentions.findIndex(
      m =>
        m.socialNetworkId === result.socialNetworkId &&
        m.externalId === result.externalId &&
        m.playerId === result.playerId
    );

    if (existingIndex >= 0) {
      toast.warning("This mention is already added for this player.");
      return;
    }

    setMentions([...mentions, result]);
  };

  const removeMention = (mention: SocialMentionData) => {
    setMentions(
      mentions.filter(
        m =>
          m.socialNetworkId !== mention.socialNetworkId ||
          m.externalId !== mention.externalId ||
          m.playerId !== mention.playerId
      )
    );
  };

  const removeMedia = (mediaItem: Media) => {
    setMediaGroup(g => {
      const newMedia = g?.media?.filter(m => m.id !== mediaItem.id);
      if (g && newMedia && newMedia.length > 0) return { ...g, media: newMedia };
      else return undefined;
    });
  };

  const changeMediaGroupType = (type: MediaType) => {
    setMediaGroup(g => {
      return g
        ? {
            ...g,
            type,
          }
        : undefined;
    });
  };

  const buildPostRequest = (): BasePostRequest => {
    const socialNetworkMessages = effectiveSocialNetworks.map(networkId => {
      const mentionNetworkId: SocialNetworksId = networkId == "IG_STORY" ? "IG" : networkId;
      const mentionsForNetwork = mentions.filter(m => m.socialNetworkId === mentionNetworkId);
      return {
        code: networkId,
        socialNetworkMessage: message,
        mentions: mentionsForNetwork,
      };
    });

    let scheduleSection = {};
    if (deliveryOptions.sendMode === "SCHEDULED") {
      scheduleSection = {
        scheduledDatetimeUtc:
          deliveryOptions.date && deliveryOptions.time
            ? promptSpecificDate(deliveryOptions.date, deliveryOptions.time)!
            : undefined,
      };
    }

    return {
      ...scheduleSection,
      sendMode: deliveryOptions.sendMode,
      messageToPost: message || "",
      mediaGroup,
      socialNetworkMessages,
      isAutoPost: deliveryOptions.isAutoPost,
    };
  };

  const validatePostRequest = (request: BasePostRequest) => {
    if (request.isAutoPost && request.socialNetworkMessages.length === 0) {
      setPostError("At least one social network must be selected");
      return false;
    }
    return true;
  };

  return {
    promptId: defaults?.promptId,
    effectivePlayerId,
    effectivePlayerUserId,
    effectivePlayer,
    onBehalfOf,
    messageTouched,
    setMessageTouched,
    resetMessageTouched,
    applyGeneratedCaption,
    defaultMessage,
    setDefaultMessage,
    addDisclaimer,
    addDefaultHashtags,
    addShortLink,
    builderLoading,
    showVideoTypeSelector: defaults?.showVideoTypeSelector || false,
    initialScript: defaults?.initialScript,
    isPosting,
    setIsPosting,
    postError,
    setPostError,
    players,
    setPlayers,
    selectedPlayerIds,
    setSelectedPlayerIds,
    selectedPlayers,
    message,
    setMessage,
    media,
    mediaGroup,
    changeMediaGroupType,
    removeMedia,
    hashtags,
    setHashtags,
    mentions,
    addMention,
    removeMention,
    deliveryOptions,
    setDeliveryOptions,
    effectiveSocialNetworks,
    effectiveTotalPostCount,
    buildPostRequest,
    validatePostRequest,
    pendingCaption,
    setPendingCaption,
    blockedWords,
    setBlockedWords,
    flaggedWords,
    setFlaggedWords,
    isDeliveryStepIncomplete,
    postButtonLabel,
  };
}

export type SharedPostFlowHook = ReturnType<typeof useSharedPostFlow>;
